import * as Yup from 'yup'

import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useFormik} from 'formik'
import clsx from 'clsx'
import {useMutation} from '@apollo/client'
import {
  BeginLoginResult,
  IResBeginPasskeyAuthentication,
  IResBeginLogin,
  IResCompletePasskeyAuthentication,
  IResCompleteLogin,
  IResSendEmailTwoFactorCode,
  TokenAuth,
} from '../../types/auth'
import {
  BEGIN_PASSKEY_AUTHENTICATION,
  BEGIN_LOGIN,
  COMPLETE_PASSKEY_AUTHENTICATION,
  COMPLETE_LOGIN,
  SEND_EMAIL_TWO_FACTOR_CODE,
} from '../../gql/mutations/authMutations'
import {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useGetUser} from '../../store/userStore'
import {STORAGE_NAMES, VERSION_APP} from '../../helpers/consts'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {
  getPasskeyAssertion,
  getPasskeyErrorMessage,
  getPasskeyUnavailableMessage,
  isPasskeySupported,
} from '../../helpers/passkeys'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const getErrorMessage = (error: any) => {
  let message = 'An error occurred. Please try again.'

  if (error?.message?.includes('Please enter valid credentials')) {
    return 'Please enter valid credentials'
  }

  if (error?.graphQLErrors?.length > 0) {
    return error.graphQLErrors[0].message
  }

  if (typeof error?.message === 'string') {
    return error.message.replace(/ApolloError.*/s, '').trim() || message
  }

  return message
}

export const LoginPage = () => {
  const defaultLogo = toAbsoluteUrl('/media/tfi/logo_policytools.png')
  const [gqlBeginLogin, {loading: loadingBegin}] = useMutation<IResBeginLogin>(BEGIN_LOGIN)
  const [gqlCompleteLogin, {loading: loadingComplete}] =
    useMutation<IResCompleteLogin>(COMPLETE_LOGIN)
  const [gqlSendEmailCode, {loading: loadingEmailCode}] =
    useMutation<IResSendEmailTwoFactorCode>(SEND_EMAIL_TWO_FACTOR_CODE)
  const [gqlBeginPasskeyAuthentication, {loading: loadingPasskeyBegin}] =
    useMutation<IResBeginPasskeyAuthentication>(BEGIN_PASSKEY_AUTHENTICATION)
  const [gqlCompletePasskeyAuthentication, {loading: loadingPasskeyComplete}] =
    useMutation<IResCompletePasskeyAuthentication>(COMPLETE_PASSKEY_AUTHENTICATION)

  const login = useGetUser((s) => s.login)
  const navigate = useNavigate()
  const location = useLocation()

  const [showPass, setShowPass] = useState(false)
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [availableMethods, setAvailableMethods] = useState<string[]>([])
  const [selectedMethod, setSelectedMethod] = useState<'APP' | 'EMAIL'>('APP')
  const [emailDestination, setEmailDestination] = useState<string | null>(null)
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const passkeySupported = useMemo(() => isPasskeySupported(), [])

  const isTwoFactorStep = Boolean(challengeId)
  const canUseEmailFallback = availableMethods.includes('EMAIL')
  const loading =
    loadingBegin || loadingComplete || loadingEmailCode || loadingPasskeyBegin || loadingPasskeyComplete

  const stepTitle = useMemo(() => {
    if (!isTwoFactorStep) return 'Sign In'
    return selectedMethod === 'EMAIL' ? 'Email Verification' : 'Authenticator Verification'
  }, [isTwoFactorStep, selectedMethod])

  const resetTwoFactorState = () => {
    setChallengeId(null)
    setAvailableMethods([])
    setSelectedMethod('APP')
    setEmailDestination(null)
    setTwoFactorCode('')
  }

  const handleAuthError = (error: any, setStatus: (message: string | null) => void) => {
    const message = getErrorMessage(error)
    const requiresPasswordChange = message.includes('You must change your password before signing in.')

    if (requiresPasswordChange) {
      const emailQuery = encodeURIComponent(formik.values.email || '')
      toast.info('You must change your password to continue.')
      navigate(`/reset-password?forced=1&email=${emailQuery}`)
      return
    }

    toast.error(message)
    setStatus(message)
  }

  const handleSuccessfulLogin = (auth: TokenAuth) => {
    login(auth)

    if (auth.user?.twoFactorRequired && !auth.user?.twoFactorEnabled) {
      toast.info('Two-factor authentication setup is required for your account.')
      navigate('/profile', {replace: true})
      return
    }

    const url = (location?.state as {prevUrl: string})?.prevUrl || '/'
    navigate(url)
  }

  const handleBeginLoginResult = (result?: BeginLoginResult | null, setStatus?: (message: string | null) => void) => {
    if (!result) {
      const message = 'Unable to start login. Please try again.'
      toast.error(message)
      setStatus?.(message)
      return
    }

    if (result.auth) {
      handleSuccessfulLogin(result.auth)
      return
    }

    if (result.requiresTwoFactor && result.challengeId) {
      setChallengeId(result.challengeId)
      setAvailableMethods(result.availableMethods || ['APP'])
      setSelectedMethod('APP')
      setEmailDestination(result.emailDestination || null)
      setTwoFactorCode('')
      setStatus?.(null)
      toast.info(result.message || 'Two-factor authentication is required.')
      return
    }

    const fallbackMessage = result.message || 'Unable to continue the login flow.'
    toast.error(fallbackMessage)
    setStatus?.(fallbackMessage)
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values, {setStatus}) => {
      setStatus(null)

      if (isTwoFactorStep) {
        if (!twoFactorCode.trim()) {
          setStatus('Verification code is required')
          return
        }

        try {
          const response = await gqlCompleteLogin({
            variables: {
              challengeId: challengeId!,
              code: twoFactorCode.trim(),
              method: selectedMethod,
            },
          })

          const auth = response.data?.completeLogin?.auth
          if (!auth) {
            const message =
              response.data?.completeLogin?.message || 'Unable to complete login. Please try again.'
            toast.error(message)
            setStatus(message)
            return
          }

          handleSuccessfulLogin(auth)
        } catch (error) {
          handleAuthError(error, setStatus)
        }

        return
      }

      try {
        const response = await gqlBeginLogin({
          variables: {
            email: values.email,
            password: values.password,
          },
        })

        handleBeginLoginResult(response.data?.beginLogin, setStatus)
      } catch (error) {
        handleAuthError(error, setStatus)
      }
    },
  })

  const handleSendEmailCode = async () => {
    if (!challengeId) return

    formik.setStatus(null)

    try {
      const response = await gqlSendEmailCode({
        variables: {
          challengeId: challengeId!,
        },
      })

      const result = response.data?.sendEmailTwoFactorCode
      if (!result) {
        throw new Error('Unable to send the email verification code.')
      }

      if (!result.success) {
        toast.info(result.message || 'Please wait before requesting another code.')
        formik.setStatus(result.message || 'Please wait before requesting another code.')
        return
      }

      setSelectedMethod('EMAIL')
      setEmailDestination(result.emailDestination || emailDestination)
      setTwoFactorCode('')
      toast.success(result.message || 'Verification code sent by email.')
    } catch (error) {
      handleAuthError(error, formik.setStatus)
    }
  }

  const handlePasskeySignIn = async () => {
    const normalizedEmail = formik.values.email.trim().toLowerCase()

    if (!normalizedEmail) {
      formik.setFieldTouched('email', true)
      formik.setStatus('Email is required to use passkey sign-in.')
      return
    }

    if (!passkeySupported) {
      const message = getPasskeyUnavailableMessage()
      toast.info(message)
      formik.setStatus(message)
      return
    }

    formik.setStatus(null)

    try {
      const beginResponse = await gqlBeginPasskeyAuthentication({
        variables: {
          email: normalizedEmail,
        },
      })

      const result = beginResponse.data?.beginPasskeyAuthentication
      if (!result?.success || !result.challengeId || !result.publicKeyOptions) {
        const message = result?.message || 'Passkey sign-in is not available for this account.'
        toast.error(message)
        formik.setStatus(message)
        return
      }

      const credential = await getPasskeyAssertion(result.publicKeyOptions)
      const completeResponse = await gqlCompletePasskeyAuthentication({
        variables: {
          challengeId: result.challengeId,
          credential,
        },
      })

      const completeResult = completeResponse.data?.completePasskeyAuthentication
      if (!completeResult?.success || !completeResult?.auth) {
        const message = completeResult?.message || 'Unable to complete passkey sign-in.'
        toast.error(message)
        formik.setStatus(message)
        return
      }

      handleSuccessfulLogin(completeResult.auth)
    } catch (error) {
      const message = getPasskeyErrorMessage(error, 'Unable to sign in with passkey.')
      const requiresPasswordChange = message.includes(
        'You must change your password before signing in.'
      )

      if (requiresPasswordChange) {
        const emailQuery = encodeURIComponent(normalizedEmail)
        toast.info('You must change your password to continue.')
        navigate(`/reset-password?forced=1&email=${emailQuery}`)
        return
      }

      toast.error(message)
      formik.setStatus(message)
    }
  }

  useEffect(() => {
    const $div = document.querySelector('#rc-widget') as HTMLDivElement
    if (!$div) return
    const str = localStorage.getItem(STORAGE_NAMES.RING_CENTRAL)
    if (str) {
      $div.style.opacity = '0'
      $div.style.pointerEvents = 'none'
    }

    return () => {
      if ($div) {
        $div.style.opacity = '1'
        $div.style.pointerEvents = 'initial'
      }
    }
  }, [])

  return (
    <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
      <div className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-10 order-2 order-lg-1'>
        <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
          <div className='w-lg-500px p-10'>
            <form
              className='form w-100'
              onSubmit={formik.handleSubmit}
              noValidate
              id='kt_login_signin_form'
            >
              <div className='text-center mb-11'>
                <img src='/media/tfi/logoNColorP.png' width={200} alt='logo' />
                <h1 className='text-dark fw-bolder mb-3'>WELCOME</h1>
                <div className='text-gray-500 fw-semibold fs-6'>{stepTitle}</div>
              </div>

              {formik.status && (
                <div className='mb-lg-15 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              )}

              {!isTwoFactorStep && (
                <>
                  <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                    <input
                      placeholder='Email'
                      {...formik.getFieldProps('email')}
                      className={clsx(
                        'form-control bg-transparent',
                        {'is-invalid': formik.touched.email && formik.errors.email},
                        {
                          'is-valid': formik.touched.email && !formik.errors.email,
                        }
                      )}
                      type='email'
                      name='email'
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className='fv-plugins-message-container'>
                        <span role='alert' className='text-danger'>
                          {formik.errors.email}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className='fv-row mb-3 position-relative'>
                    <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder='Password'
                      autoComplete='off'
                      {...formik.getFieldProps('password')}
                      className={clsx(
                        'form-control bg-transparent',
                        {
                          'is-invalid': formik.touched.password && formik.errors.password,
                        },
                        {
                          'is-valid': formik.touched.password && !formik.errors.password,
                        }
                      )}
                    />
                    <button
                      type='button'
                      onClick={() => setShowPass((p) => !p)}
                      style={{
                        color: '#111',
                        border: 0,
                        backgroundColor: 'transparent',
                        margin: 0,
                        padding: 0,
                        position: 'absolute',
                        right:
                          formik.touched.password && formik.errors.password
                            ? '36px'
                            : formik.touched.password
                              ? '36px'
                              : '10px',
                        bottom: formik.touched.password && formik.errors.password ? '35px' : '10px',
                      }}
                    >
                      {showPass ? (
                        <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                      ) : (
                        <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                      )}
                    </button>
                    {formik.touched.password && formik.errors.password && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.password}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
                    <div />
                    <Link to='/forgot-password' className='link-primary'>
                      I forgot my password
                    </Link>
                  </div>
                </>
              )}

              {isTwoFactorStep && (
                <>
                  <div className='mb-8'>
                    <div className='text-gray-700 fw-semibold mb-2'>
                      {selectedMethod === 'EMAIL'
                        ? `Enter the code sent to ${emailDestination || 'your email'}.`
                        : 'Enter the 6-digit code from your authenticator app.'}
                    </div>
                    <div className='text-gray-500 fs-7'>
                      Your password was accepted. We need one more verification step.
                    </div>
                  </div>

                  <div className='fv-row mb-6 position-relative'>
                    <label className='form-label fw-bolder text-dark fs-6 mb-0'>
                      {selectedMethod === 'EMAIL' ? 'Email code' : 'Authenticator code'}
                    </label>
                    <input
                      type='tel'
                      name='twoFactorCode'
                      placeholder='000000'
                      inputMode='numeric'
                      autoComplete='off'
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className='form-control bg-transparent'
                    />
                  </div>

                  <div className='d-flex flex-column gap-2 fs-7 fw-semibold mb-8'>
                    {canUseEmailFallback && selectedMethod !== 'EMAIL' && (
                      <button
                        type='button'
                        className='btn btn-link p-0 text-start align-self-start'
                        onClick={handleSendEmailCode}
                        disabled={loading}
                      >
                        I can&apos;t use my authenticator app. Send a code by email.
                      </button>
                    )}
                    {selectedMethod === 'EMAIL' && (
                      <>
                        <button
                          type='button'
                          className='btn btn-link p-0 text-start align-self-start'
                          onClick={() => {
                            setSelectedMethod('APP')
                            setTwoFactorCode('')
                            formik.setStatus(null)
                          }}
                          disabled={loading}
                        >
                          Use authenticator app instead
                        </button>
                        <button
                          type='button'
                          className='btn btn-link p-0 text-start align-self-start'
                          onClick={handleSendEmailCode}
                          disabled={loading}
                        >
                          Resend email code
                        </button>
                      </>
                    )}
                    <button
                      type='button'
                      className='btn btn-link p-0 text-start align-self-start'
                      onClick={() => {
                        resetTwoFactorState()
                        formik.setStatus(null)
                      }}
                      disabled={loading}
                    >
                      Use another account
                    </button>
                  </div>
                </>
              )}

              <div className='d-grid mb-10'>
                <button
                  type='submit'
                  id='kt_sign_in_submit'
                  className='btn btn-primary mt-4'
                  disabled={
                    formik.isSubmitting ||
                    loading ||
                    (!isTwoFactorStep && !formik.isValid) ||
                    (isTwoFactorStep && !twoFactorCode.trim())
                  }
                >
                  {!loading && (
                    <span className='indicator-label'>
                      {isTwoFactorStep ? 'Verify and continue' : 'Continue'}
                    </span>
                  )}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>

              {!isTwoFactorStep && (
                <div className='d-grid mb-10'>
                  <button
                    type='button'
                    className='btn btn-light-primary'
                    onClick={handlePasskeySignIn}
                    disabled={loading || !passkeySupported}
                  >
                    Sign in with passkey
                  </button>
                  {!passkeySupported && (
                    <div className='text-muted fs-7 mt-3'>{getPasskeyUnavailableMessage()}</div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>

        <div className='d-flex flex-center flex-wrap px-5'>
          <div>
            <span>{VERSION_APP}</span>
          </div>
        </div>
      </div>

      <div
        className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2'
        style={{backgroundImage: `url(${toAbsoluteUrl('/media/misc/auth-bg.png')})`}}
      >
        <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
          <span className='mb-12'>
            <img alt='Logo' src={defaultLogo} className='h-75px' />
          </span>

          <img
            className='mx-auto w-275px w-md-60 w-xl-500px mb-10 mb-lg-20'
            src={toAbsoluteUrl('/media/tfi/login.png')}
            alt=''
          />

          <h1 className='text-white fs-2qx fw-bolder text-center mb-7'>
            Fast, Efficient and Productive
          </h1>
        </div>
      </div>
    </div>
  )
}
